import {
  FETCH_SCHEDULED_BOOKINGS_REQUEST,
  FETCH_SCHEDULED_BOOKINGS_SUCCESS,
  FETCH_SCHEDULED_BOOKINGS_FAIL,
  CREATE_SCHEDULED_BOOKING_REQUEST,
  CREATE_SCHEDULED_BOOKING_SUCCESS,
  CREATE_SCHEDULED_BOOKING_FAIL,
  UPDATE_SCHEDULED_BOOKING_REQUEST,
  UPDATE_SCHEDULED_BOOKING_SUCCESS,
  UPDATE_SCHEDULED_BOOKING_FAIL,
  CANCEL_SCHEDULED_BOOKING_REQUEST,
  CANCEL_SCHEDULED_BOOKING_SUCCESS,
  CANCEL_SCHEDULED_BOOKING_FAIL,
  SET_ACTIVE_SCHEDULED_BOOKING,
  CLEAR_ACTIVE_SCHEDULED_BOOKING,
  ADD_RESOURCE_ASSIGNMENT,
  REMOVE_RESOURCE_ASSIGNMENT,
  UPDATE_RESOURCE_ASSIGNMENT,
  CLEAR_SCHEDULED_BOOKING_ERROR,
} from "../constants/ScheduledBookingConstants";
import { getScheduledBookings } from "../services/bookingService";

const buildScheduledBookingId = (bookedDate) => {
  const date = bookedDate ? new Date(bookedDate) : new Date();
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const rand = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
  return `SB-${yyyy}-${mm}-${dd}-${rand}`;
};

export const fetchScheduledBookings = () => async (dispatch, getState) => {
  dispatch({ type: FETCH_SCHEDULED_BOOKINGS_REQUEST });

  try {
    // Fetch scheduled bookings only (BookingStatus === "Shedule Booking")
    const apiBookings = await getScheduledBookings();
    
    // Already filtered for scheduled bookings from the API
    const scheduledBookings = apiBookings || [];
    console.log("[fetchScheduledBookings] Fetched scheduled bookings from API:", scheduledBookings.length, scheduledBookings);

    // Helper to parse date from API format (e.g. "11/3/2025 9:54:57 AM")
    const parseDate = (dateStr) => {
      if (!dateStr) return "";
      const parts = dateStr.split(" ");
      return parts[0] || ""; // returns "11/3/2025"
    };

    // Helper to parse time from API format
    const parseTime = (dateStr) => {
      if (!dateStr) return "";
      const parts = dateStr.split(" ");
      if (parts[1]) {
        // Extract HH:MM from "9:54:57"
        const timeParts = parts[1].split(":");
        if (timeParts.length >= 2) {
          const hour = timeParts[0].padStart(2, "0");
          const min = timeParts[1].padStart(2, "0");
          return `${hour}:${min}`;
        }
      }
      return "";
    };

    // Map API response to match our expected format
    const mappedBookings = scheduledBookings.map((b) => {
      const subBookRaw = String(b.SubBook || "").trim().toUpperCase();
      const hasResources =
        subBookRaw === "A" ||
        subBookRaw === "AVEALABLE" ||
        subBookRaw === "AVAILABLE";

      return {
        ...b,
        scheduledBookingId: b.bId || b.scheduledBookingId,
        vehicleId: b.vId || b.vehicleId,
        vehicleName: b.vId || b.vehicleName,
        bookedDate: parseDate(b.bsTime),
        startTime: parseTime(b.bsTime),
        endTime: parseTime(b.beTime),
        workArea: b.WorkArea || "",
        workScope: b.WorkScope || "",
        fromLocation: b.From || "",
        toLocation: b.TO || "",
        priority: b.Priority || "",
        remark: b.Remark || "",
        bookedBy: b.bookingSno || "",
        status: b.status || "Scheduled",
        needsResources: hasResources,
      };
    });

    const sorted = [...mappedBookings].sort((a, b) => {
      const aId = parseInt(a.bId || a.scheduledBookingId || 0);
      const bId = parseInt(b.bId || b.scheduledBookingId || 0);
      return bId - aId;
    });

    dispatch({
      type: FETCH_SCHEDULED_BOOKINGS_SUCCESS,
      payload: sorted,
    });
    return sorted;
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Failed to fetch scheduled bookings.";
    dispatch({
      type: FETCH_SCHEDULED_BOOKINGS_FAIL,
      payload: message,
    });
    throw error;
  }
};

export const createScheduledBooking = (bookingData) => async (dispatch, getState) => {
  dispatch({ type: CREATE_SCHEDULED_BOOKING_REQUEST });

  try {
    const { userDetails } = getState()?.auth || {};
    const timestamp = Date.now();

    const newBooking = {
      ...bookingData,
      scheduledBookingId: buildScheduledBookingId(bookingData?.bookedDate),
      bookedBy: userDetails?.ServiceNo || userDetails?.serviceNo || "UNKNOWN",
      status: "Scheduled",
      resources: bookingData?.resources || [],
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    dispatch({
      type: CREATE_SCHEDULED_BOOKING_SUCCESS,
      payload: newBooking,
    });
    return newBooking;
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Failed to create scheduled booking.";
    dispatch({
      type: CREATE_SCHEDULED_BOOKING_FAIL,
      payload: message,
    });
    throw error;
  }
};

export const updateScheduledBooking = (scheduledBookingId, updatedData) => async (
  dispatch,
  getState
) => {
  dispatch({ type: UPDATE_SCHEDULED_BOOKING_REQUEST });

  try {
    const current = (getState()?.scheduledBooking?.items || []).find(
      (item) => item.scheduledBookingId === scheduledBookingId
    );
    const updatedBooking = {
      ...current,
      ...updatedData,
      scheduledBookingId,
      updatedAt: Date.now(),
    };

    dispatch({
      type: UPDATE_SCHEDULED_BOOKING_SUCCESS,
      payload: updatedBooking,
    });
    return updatedBooking;
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Failed to update scheduled booking.";
    dispatch({
      type: UPDATE_SCHEDULED_BOOKING_FAIL,
      payload: message,
    });
    throw error;
  }
};

export const cancelScheduledBooking = (scheduledBookingId) => async (dispatch) => {
  dispatch({
    type: CANCEL_SCHEDULED_BOOKING_REQUEST,
    payload: scheduledBookingId,
  });

  try {
    dispatch({
      type: CANCEL_SCHEDULED_BOOKING_SUCCESS,
      payload: scheduledBookingId,
    });
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Failed to cancel scheduled booking.";
    dispatch({
      type: CANCEL_SCHEDULED_BOOKING_FAIL,
      payload: {
        scheduledBookingId,
        message,
      },
    });
    throw error;
  }
};

export const setActiveScheduledBooking = (booking) => ({
  type: SET_ACTIVE_SCHEDULED_BOOKING,
  payload: booking,
});

export const clearActiveScheduledBooking = () => ({
  type: CLEAR_ACTIVE_SCHEDULED_BOOKING,
});

export const addResourceAssignment = (resource) => ({
  type: ADD_RESOURCE_ASSIGNMENT,
  payload: resource,
});

export const removeResourceAssignment = (resourceId) => ({
  type: REMOVE_RESOURCE_ASSIGNMENT,
  payload: resourceId,
});

export const updateResourceAssignment = (resource) => ({
  type: UPDATE_RESOURCE_ASSIGNMENT,
  payload: resource,
});

export const clearScheduledBookingError = () => ({
  type: CLEAR_SCHEDULED_BOOKING_ERROR,
});
